/**
 * Portal logins, as every screen that shows or hands one out reads them.
 *
 * Two things live here: which of a driver's logins is "theirs", and the
 * API's refusal when an address already belongs to somebody else's login.
 *
 * WHICH LOGIN IS THEIRS — one rule for the operator portal's Drivers &
 * vehicles (Fleet.jsx) and the admin's "Assign driver & car" dialog
 * (driverHandover.js).
 *
 * A driver can have more than one row: the table does not enforce one login
 * per driver, and a revoked login keeps its row when a new one is granted
 * (to another address, say). So "their" login is the ACTIVE one, else the
 * most recent — never simply the first row that matches.
 *
 * Both row shapes are read: the portal API's (`driver_id`, `active: 1`,
 * `enrolment_pending`), as the operator portal gets them, and
 * PortalUsersService's (`driverId`, `active: true`, `enrolmentPending`), as
 * the admin screens get them.
 */

/** What a driver's access is: no login, invited (link not used yet), active, revoked. */
export const LOGIN_STATES = {
  NONE: 'none', INVITED: 'invited', ACTIVE: 'active', REVOKED: 'revoked',
};

const driverIdOf = (login) => login?.driver_id ?? login?.driverId;
const isActive = (login) => login?.active === 1 || login?.active === true;
const isPending = (login) => Boolean(login?.enrolment_pending ?? login?.enrolmentPending);

/** The driver's login: the active one, else the most recent, else null. */
export const driverLoginOf = (logins, driverId) => {
  if (driverId === undefined || driverId === null || driverId === '') return null;

  const own = (logins || []).filter((login) => String(driverIdOf(login)) === String(driverId));
  const newest = [...own].sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));

  return newest.find(isActive) || newest[0] || null;
};

/** One of LOGIN_STATES for a login (or for none). */
export const loginState = (login) => {
  if (!login) return LOGIN_STATES.NONE;
  if (!isActive(login)) return LOGIN_STATES.REVOKED;
  return isPending(login) ? LOGIN_STATES.INVITED : LOGIN_STATES.ACTIVE;
};

// ==================== An address somebody else's login holds ====================
//
// The portal Cognito pool spans every operator, so one address is one login,
// full stop. Granting new access on an address a DIFFERENT login already
// holds is refused by the API with this code (ph-core
// Services/PortalEnrolmentService.ts, PortalEmailHeldError) and a `details`
// payload naming the holder. Staff may replace a REVOKED holder by sending
// the grant again with `replace_existing`; an ACTIVE one is never
// replaceable and comes back with `replaceable: false` and a message saying
// it must be revoked first.
//
// This is admin-only: the operator portal's own invite endpoint answers one
// generic sentence with no code, so none of this can fire there.

/** The API's machine-readable "this address is taken" code. */
export const PORTAL_EMAIL_HELD = 'PORTAL_EMAIL_HELD';

/**
 * How a grant's kind reads as the access being handed out. Only the two
 * kinds that can still be granted — office staff sign in with their
 * dashboard account, so no dispatch access is ever handed out.
 */
const ACCESS_WORDS = {
  operator: 'operator access',
  driver: 'driver access',
};

/**
 * How a HOLDER's kind reads as the login being replaced. 'dispatcher' stays
 * here even though none can be granted any more: the rows from before are
 * still in the table, so one can still be sitting on an address somebody is
 * being granted access to, and the API still names it as the holder.
 */
const LOGIN_WORDS = {
  operator: 'operator login',
  driver: 'driver login',
  dispatcher: 'dispatch login',
};

/**
 * The held-address payload of a failed grant, or null for every other
 * error. Keyed on the CODE — never on the message, which is written for a
 * person and free to be reworded.
 *
 * @param err an axios error from PortalUsersService.grant
 * @returns `{ email, replaceable, holder }`, where holder is
 *   `{ kind, operator_id?, operator_name?, driver_id?, active }`
 */
export const emailHeldBy = (err) => {
  const body = err?.response?.data;

  return body?.code === PORTAL_EMAIL_HELD && body.details?.holder ? body.details : null;
};

/**
 * What replacing the holder will do, in plain English — what is being
 * destroyed, whose it is, and what takes its place. The one sentence both
 * the dispatch panel and the operator modal confirm with.
 *
 * @param held the payload from `emailHeldBy`
 * @param grantingKind the kind of login being granted now
 */
export const replaceWarning = (held, grantingKind) => {
  const { email, holder } = held;
  const whose = holder.operator_name || holder.operator_id;
  const login = LOGIN_WORDS[holder.kind] || `${holder.kind} login`;
  const access = ACCESS_WORDS[grantingKind] || 'this access';

  return `${email} already has a revoked ${login}${whose ? ` for ${whose}` : ''}. `
    + `Replacing it with ${access} will remove that login for good.`;
};
